import {createFeatureSelector, createSelector} from '@ngrx/store';
import {ISettingState} from './setting.reducer';
import {UserProfileSettingsComponent} from '../components/user-profile-settings/user-profile-settings.component';
import {
  IAddItemRule,
  IAutoOrderOrderingSettings,
  ICopyStoreSettingsResult,
  IDisableStockItemRule, IEmailSettings,
  IEnableStockItemRule,
  IGlobalStockTag,
  IMultiplePriceChangeRulesStoreSettings,
  IOpenOrderSettings,
  IPriceBandingStoreSettings,
  IStockItemEditThresholds,
  IStoreOverrideSettings,
  ITableNavSettings,
  normalizeDisableStockItemRuleFromFirestore,
  SettingsReferenceEnum,
  TLineColour,
  TLocaleDateFormat,
  TStockTag,
  TStoreDisplayOrder
} from '../models';
import {UserTableSettingsComponent} from '../components/user-table-settings/user-table-settings.component';
import {ApplicationPreferencesComponent} from '../components/application-preferences/application-preferences.component';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {SettingsState} from '../../settings/store/settings.reducer';
import {selectPriceBandingForAllStores} from '../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {IPriceBand, IPriceBandPerStore} from '../../../shared/shared-models/price-banding/price-band';
import {normalizePriceBandsDocument} from '../../../shared/shared-utils/price/price-banding.utils';
import {
  createDefaultMultiplePriceChangeSettings,
} from '../../../shared/shared-utils/price/multiple-price-change.utils';
import {
  FeatureShelfTalkersGeneralComponent,
} from '../components/feature-shelf-talkers-general/feature-shelf-talkers-general.component';
import {
  FeatureStockTakeGeneralComponent,
} from '../components/feature-stock-take-general/feature-stock-take-general.component';
import {FeatureStockRuleAddComponent} from '../components/feature-stock-rule-add/feature-stock-rule-add.component';
import {
  FeatureStockRuleEnableComponent,
} from '../components/feature-stock-rule-enable/feature-stock-rule-enable.component';
import {
  FeatureStockRuleDisableComponent,
} from '../components/feature-stock-rule-disable/feature-stock-rule-disable.component';
import {FeatureStockTagsComponent} from '../components/feature-stock-tags/feature-stock-tags.component';
import {
  FeatureStockPriceThresholdComponent,
} from '../components/feature-stock-price-threshold/feature-stock-price-threshold.component';
import {
  FeatureAutoOrderGeneralSettingsComponent,
} from '../components/feature-auto-order-general-settings/feature-auto-order-general-settings.component';
import {
  FeatureAutoOrderOpenOrderSettingsComponent,
} from '../components/feature-auto-order-open-order-settings/feature-auto-order-open-order-settings.component';
import {
  FeatureAutoOrderDocumentLayoutsComponent,
} from '../components/feature-auto-order-document-layouts/feature-auto-order-document-layouts.component';
import {
  resolveStockTakeDefaultMaxItemLimit,
  STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT,
} from '../../stock-take/utils/stock-take-limits.utils';
import {
  FeatureLocalStorageManagerComponent,
} from '../components/feature-local-storage-manager/feature-local-storage-manager.component';
import {FeatureLabelMakerComponent} from '../components/feature-label-maker/feature-label-maker.component';
import {EmailSettingsComponent} from '../components/email-settings/email-settings.component';
import {
  FeatureAdvancedFiltersComponent,
} from '../components/feature-advanced-filters/feature-advanced-filters.component';
import {
  FeatureCopySettingsComponent,
} from '../components/feature-copy-settings/feature-copy-settings.component';
import {
  FeatureStockPriceBandingComponent,
} from '../components/feature-stock-price-banding/feature-stock-price-banding.component';
import {
  FeatureStockMultiplePriceChangesComponent,
} from '../components/feature-stock-multiple-price-changes/feature-stock-multiple-price-changes.component';
import {selectUser} from '../../user-access/user-store/user.selectors';
import {selectColleagues} from '../../core/store-shared/shared.selectors';
import {IColleagues, IColleagueUser, IUser, IUserAccess} from '../../user-access/models';
import {IStore} from '../../../shared/shared-models/store/store';
import {IProcessedOrderSettings} from '../models/auto-ordering/ao-processed-order-settings.interface';
import {
  selectUserAccess,
} from '../../user-access/user-access-store/user-access.selectors';
import {isStoreAdminForStore} from '../../../shared/shared-utils/user-access/user-access.utils';

export const selectSettingState = createFeatureSelector<ISettingState>('setting');

//#region SELECT - SELECTED ITEMS
export const selectSelectedSetting = createSelector(
  selectSettingState,
  (state: ISettingState): SettingsReferenceEnum => {
    return state.selectedSettingRef ?? SettingsReferenceEnum.undefined;
  },
);

export const selectSelectedNavigationSetting = createSelector(
  selectSettingState,
  (state: ISettingState): SettingsReferenceEnum => {
    return state.selectedSettingNavigationRef ?? SettingsReferenceEnum.undefined;
  },
);

export const selectSelectedSection = createSelector(
  selectSettingState,
  (state: ISettingState): string => {
    return state.selectedSection ?? '';
  },
);

export const selectSelectedSubSection = createSelector(
  selectSettingState,
  (state: ISettingState): string => {
    return state.selectedSubSection ?? '';
  },
);
//#endregion

//#region SELECT - SELECTED SETTING - COMPONENT
export const selectSelectedSettingComponent = createSelector(
  selectSelectedNavigationSetting,
  (setting: number) => {
    let selectedComponent = undefined;
    switch (setting) {
      case 101:
        selectedComponent = UserProfileSettingsComponent;
        break;
      case 201:
        selectedComponent = UserTableSettingsComponent;
        break;
      case 202:
        selectedComponent = ApplicationPreferencesComponent;
        break;
      case 204:
        selectedComponent = EmailSettingsComponent;
        break;
      case 401:
      case 403:
      case 404:
        selectedComponent = FeatureAutoOrderGeneralSettingsComponent;
        break;
      case 402:
        selectedComponent = FeatureAutoOrderOpenOrderSettingsComponent;
        break;
      case 405:
        selectedComponent = FeatureAutoOrderDocumentLayoutsComponent;
        break;
      case 601:
        selectedComponent = FeatureShelfTalkersGeneralComponent;
        break;
      case 602:
        selectedComponent = FeatureLabelMakerComponent;
        break;
      case 701:
        selectedComponent = FeatureStockRuleAddComponent;
        break;
      case 702:
        selectedComponent = FeatureStockRuleEnableComponent;
        break;
      case 703:
        selectedComponent = FeatureStockRuleDisableComponent;
        break;
      case 704:
        selectedComponent = FeatureStockTagsComponent;
        break;
      case 705:
        selectedComponent = FeatureStockPriceThresholdComponent;
        break;
      case 706:
        selectedComponent = FeatureAdvancedFiltersComponent;
        break;
      case 707:
        selectedComponent = FeatureStockPriceBandingComponent;
        break;
      case 708:
        selectedComponent = FeatureStockMultiplePriceChangesComponent;
        break;
      case 801:
        selectedComponent = FeatureLocalStorageManagerComponent;
        break;
      case 900:
        selectedComponent = FeatureCopySettingsComponent;
        break;
      case 1001:
        selectedComponent = FeatureStockTakeGeneralComponent;
        break;
    }
    return selectedComponent;
  },
);

//#endregion

//#region SELECT - APPLICATION SETTINGS
export const selectApplicationSettings = createSelector(
  selectSettingState,
  (state: ISettingState) => {
    return state.application;
  },
);

export const selectApplicationUserTableSettings = createSelector(
  selectApplicationSettings,
  (app): ITableNavSettings => {
    return app.tableSettings;
  },
);

export const selectApplicationPreferenceSettings = createSelector(
  selectApplicationSettings,
  (app): { dateFormat: TLocaleDateFormat } => {
    return app.preferences;
  },
);

export const selectStoreDisplayOrder = createSelector(
  selectApplicationSettings,
  (app): TStoreDisplayOrder => {
    return app.preferences.storeDisplayOrder;
  },
);

export const selectCustomStoreOrder = createSelector(
  selectApplicationSettings,
  (app): string[] => {
    return app.preferences.customStoreOrder;
  },
);
//#endregion

//#region SELECT - FEATURE SETTINGS
export const selectFeatureSettings = createSelector(
  selectSettingState,
  (state: ISettingState) => {
    return state.feature;
  },
);

export const selectNgpReportEditsPreview = createSelector(
  selectFeatureSettings,
  (feature): { [index: number]: string } => {
    return feature.ngpReport.editsPreview;
  },
);

export const selectStockManagerEditsPreview = createSelector(
  selectFeatureSettings,
  (feature): { [index: number]: string } => {
    return feature.stockManager.editsPreview;
  },
);
//#endregion

//#region SELECT - GENERAL SETTINGS
export const selectFeatureGeneralSettings = createSelector(
  selectFeatureSettings,
  (feature) => {
    return feature.general;
  },
);
//#endregion

//#region SELECT - AUTO ORDER SETTINGS
export const selectFeatureAutoOrderSettings = createSelector(
  selectFeatureSettings,
  (feature) => {
    return feature.autoOrder;
  },
);

export const selectSelectedStoreOrderSettings = createSelector(
  selectFeatureAutoOrderSettings,
  selectSelectedStore,
  (autoOrder, store): IAutoOrderOrderingSettings => {
    return autoOrder.storeOrderSettings?.[store.storeId] ?? {} as IAutoOrderOrderingSettings;
  },
);

export const selectSelectedStoreOpenOrderSettings = createSelector(
  selectFeatureAutoOrderSettings,
  selectSelectedStore,
  (autoOrder, store): IOpenOrderSettings => {
    return autoOrder.storeOpenOrderSettings?.[store.storeId] ?? {} as IOpenOrderSettings;
  },
);

export const selectSelectedStoreAutoOrderOverrideSettings = createSelector(
  selectFeatureGeneralSettings,
  selectSelectedStore,
  (settings, store): IStoreOverrideSettings => {
    return settings.overrides?.[store.storeId] ?? {
      orderBlankSupplierCode: false,
    } as IStoreOverrideSettings;
  },
);

export const selectSelectedStoreAutoOrderOrderProcessedSettings = createSelector(
  selectFeatureGeneralSettings,
  selectSelectedStore,
  (settings, store): IProcessedOrderSettings => {
    return settings.priceEdits?.[store.storeId] ?? {
      priceEditsAfterProcessed: false,
    } as IProcessedOrderSettings;
  },
);

//#endregion

//#region SELECT - SHELF TALKER SETTINGS
export const selectFeatureShelfTalkerSettings = createSelector(
  selectFeatureSettings,
  (feature) => {
    return feature.shelfTalkers;
  },
);

export const selectSelectedStoreShelfTalkerOverrideSettings = createSelector(
  selectFeatureGeneralSettings,
  selectSelectedStore,
  (settings, store): IStoreOverrideSettings => {
    return settings.overrides?.[store.storeId] ?? {
      shelfTalkersEnabled: true,
    } as IStoreOverrideSettings;
  },
);

/**
 * Whether new stock takes should include Del-Col by default for the selected store.
 * Missing Firestore value is treated as enabled (true).
 */
export const selectSelectedStoreIncludeDelColDefault = createSelector(
  selectFeatureGeneralSettings,
  selectSelectedStore,
  (settings, store): boolean => {
    if (!store?.storeId) {
      return true;
    }
    return settings.overrides?.[store.storeId]?.includeDelColDefault !== false;
  },
);

/**
 * Default max items for new stock takes / schedules for the selected store.
 * Missing Firestore value uses the app default (250); hard max remains 500.
 */
export const selectSelectedStoreDefaultMaxItemLimit = createSelector(
  selectFeatureGeneralSettings,
  selectSelectedStore,
  (settings, store): number => {
    if (!store?.storeId) {
      return STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT;
    }
    return resolveStockTakeDefaultMaxItemLimit(settings.overrides?.[store.storeId]?.defaultMaxItemLimit);
  },
);

//#endregion

//#region SELECT - STOCK RULES SETTINGS
export const selectFeatureStockRulesSettings = createSelector(
  selectFeatureSettings,
  (feature) => {
    return feature.stockRules;
  },
);

export const selectSelectedStoreAddItemRule = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): IAddItemRule => {
    return stockRules.stockItemAdd?.[store.storeId] ?? {
      digits: 0,
      manualCodeEntryDefault: false,
      prefix: '',
      suffix: '',
    };
  },
);

export const selectAllStoresAddItemRules = createSelector(
  selectFeatureStockRulesSettings,
  (stockRules): Record<string, IAddItemRule> => stockRules.stockItemAdd ?? {},
);

export const selectSelectedStoreEnableItemRule = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): IEnableStockItemRule => {
    return stockRules.stockItemEnable?.[store.storeId] ?? {
      descPrefixArray: [],
      descSuffixArray: [],
      lineColour: 0,
      onHoldCode: 0,
    };
  },
);

export const selectSelectedStoreDisableItemRule = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): IDisableStockItemRule => {
    return normalizeDisableStockItemRuleFromFirestore(stockRules.stockItemDisable?.[store.storeId]);
  },
);

export const selectSelectedStoreLineColours = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): TLineColour => {
    return stockRules.lineColours?.[store.storeId] ?? {};
  },
);

export const selectAllStoresLineColours = createSelector(
  selectFeatureStockRulesSettings,
  (stockRules): { [storeId: string]: TLineColour } => {
    return stockRules.lineColours ?? {};
  },
);

export const selectSelectedStoreStockTags = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): IGlobalStockTag[] => {
    const globalTags: IGlobalStockTag[] = (stockRules.globalStockTags || []).map(
      (tag: TStockTag): IGlobalStockTag => ({
        global: true,
        name: tag,
      }),
    );

    const storeTags: IGlobalStockTag[] = (stockRules.stockTagsSetting?.[store.storeId] || []).map(
      (tag: TStockTag): IGlobalStockTag => ({
        global: false,
        name: tag,
      }),
    );

    return [...globalTags, ...storeTags];
  },
);

export const selectSelectedStoreStockThreshold = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store): IStockItemEditThresholds => {
    return stockRules.stockItemEditThreshold?.[store.storeId] ?? {
      sellPriIncl1: {
        pct: 0,
        negPct: 0,
      },
    };
  },
);

const selectLegacySettingsState = createFeatureSelector<SettingsState>('settings');

const selectLegacyStorePriceBands = createSelector(
  selectLegacySettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): Record<number, IPriceBand> | IPriceBand[] | undefined => {
    if (!store?.storeId) {
      return undefined;
    }
    return state?.storeSettings?.[store.storeId]?.priceBands;
  },
);

const selectSharedGridStorePriceBands = createSelector(
  selectPriceBandingForAllStores,
  selectSelectedStore,
  (priceBands: IPriceBandPerStore, store: IStore): IPriceBand[] => {
    if (!store?.storeId || !priceBands) {
      return [];
    }
    const bands: IPriceBand[] | undefined = priceBands[store.storeId];
    return Array.isArray(bands) ? bands : [];
  },
);

export const selectSelectedStorePriceBands = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  selectLegacyStorePriceBands,
  selectSharedGridStorePriceBands,
  (
    stockRules,
    store,
    legacyBands: Record<number, IPriceBand> | IPriceBand[] | undefined,
    sharedGridBands: IPriceBand[],
  ): IPriceBandingStoreSettings => {
    const storeSettings: IPriceBandingStoreSettings | undefined = stockRules.priceBands?.[store?.storeId];
    if (storeSettings) {
      return storeSettings;
    }

    const globalBands: IPriceBand[] = stockRules.globalPriceBands ?? [];
    if (globalBands.length) {
      return {
        bands: globalBands,
        source: 'global',
      };
    }

    const legacy: IPriceBand[] = normalizePriceBandsDocument(legacyBands);
    if (legacy.length) {
      return {
        bands: legacy,
        source: 'store',
      };
    }

    const sharedGrid: IPriceBand[] = Array.isArray(sharedGridBands) ? sharedGridBands : [];
    if (sharedGrid.length) {
      return {
        bands: sharedGrid,
        source: 'store',
      };
    }

    return {
      bands: [],
      source: storeSettings?.source ?? 'global',
    };
  },
);

export const selectPriceBandsByStore = createSelector(
  selectFeatureStockRulesSettings,
  (stockRules): Record<string, IPriceBandingStoreSettings> => stockRules.priceBands ?? {},
);

export const selectGlobalPriceBands = createSelector(
  selectFeatureStockRulesSettings,
  (stockRules): IPriceBandingStoreSettings['bands'] => stockRules.globalPriceBands ?? [],
);

export const selectIsPriceBandsLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.priceBands,
);

export const selectIsPriceBandsSaving = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.priceBandsSave,
);

export const selectSelectedStoreMultiplePriceChangeRules = createSelector(
  selectFeatureStockRulesSettings,
  selectSelectedStore,
  (stockRules, store: IStore): IMultiplePriceChangeRulesStoreSettings =>
    stockRules.multiplePriceChangeRules?.[store?.storeId] ?? createDefaultMultiplePriceChangeSettings(),
);

export const selectIsMultiplePriceChangeRulesLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.multiplePriceChangeRules,
);

export const selectIsMultiplePriceChangeRulesSaving = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.multiplePriceChangeRulesSave,
);

export const selectIsCopyingStoreSettings = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.copyStoreSettings,
);

export const selectCopyStoreSettingsResult = createSelector(
  selectSettingState,
  (state: ISettingState): ICopyStoreSettingsResult | null => state.copyStoreSettingsResult,
);

//#endregion

export const selectAllUsersEmailSettingForCurrentSelectedStore = createSelector(
  selectSettingState,
  selectSelectedStore,
  (state: ISettingState, store: IStore): Record<string, IEmailSettings> =>
    state.feature.email.usersEmailSettings?.[store.storeId],
);

export const selectUsersMailerDocumentExistsForCurrentStore = createSelector(
  selectSettingState,
  selectSelectedStore,
  (state: ISettingState, store: IStore): Record<string, boolean> =>
    state.feature.email.usersMailerDocumentExists?.[store.storeId] ?? {},
);

export const selectCurrentUserEmailSettings = createSelector(
  selectAllUsersEmailSettingForCurrentSelectedStore,
  selectUser,
  (emailSettings: Record<string, IEmailSettings>, user: IUser): IEmailSettings =>
    emailSettings?.[user?.id],
);

export const selectUserEmailSettingsIsSubmitting = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.submittingEmailSettings,
);

export const selectIsUserEmailsLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.userEmailSettings,
);

export const selectIsSelectedUserEmailSettingsLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.submittingEmailSettings,
);

export const selectIsOrderSettingsLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.orderSettings,
);

export const selectIsAddItemRulesLoading = createSelector(
  selectSettingState,
  (state: ISettingState): boolean => state.isLoading.addStockItemRules,
);

export const selectMicrosoftMailBusyLabel = createSelector(
  selectSettingState,
  (state: ISettingState): string | null => state.feature.email.microsoftMailBusyLabel,
);

export const selectEmailSettingsView = createSelector(
  selectUser,
  selectColleagues,
  selectSelectedStore,
  selectAllUsersEmailSettingForCurrentSelectedStore,
  selectUsersMailerDocumentExistsForCurrentStore,
  selectUserEmailSettingsIsSubmitting,
  selectIsUserEmailsLoading,
  selectUserAccess,
  selectMicrosoftMailBusyLabel,
  (
    currentUser: IUser,
    colleagues: IColleagues,
    selectedStore: IStore,
    emailSettings: Record<string, IEmailSettings>,
    mailerDocumentExists: Record<string, boolean>,
    isSubmitting: boolean,
    isLoadingSettings: boolean,
    userAccess: IUserAccess,
    microsoftMailBusyLabel: string | null,
  ) => {
    const canManageColleagueEmails: boolean = isStoreAdminForStore(
      userAccess,
      selectedStore?.storeId,
    );
    const eligibleUsers: IUser[] = canManageColleagueEmails
      ? [
        currentUser,
        ...Object.values(colleagues)
          .filter((colleague: IColleagueUser) =>
            colleague.userData !== null &&
            colleague.storeList?.includes(selectedStore?.storeId),
          )
          .map((colleague: IColleagueUser) => colleague.userData),
      ]
      : (currentUser ? [currentUser] : []);
    return {
      colleagues: eligibleUsers,
      canManageColleagueEmails,
      emailSettings,
      mailerDocumentExists,
      selectedStore,
      isSubmitting,
      isLoadingSettings,
      signedInUserId: currentUser?.id,
      microsoftMailBusyLabel,
    };
  },
);